/**
 * instructorTotal — the instructor's logged time on a completed event.
 *
 * Legacy parity (`calendar.event-view.js:454-456`): once an event is checked
 * in, the Instructor line reads `Ngawang Sherpa (Total: 3.00)`. The number
 * was `event.resourcesDiff[instructorID]` — the instructor RESOURCE's
 * `diffCounter`, which the legacy PHP detail computed for every resource on
 * the event (`CalendarForOrganization.php:5878`). The V2 modal dropped it
 * (owner, 2026-09-16).
 *
 * The Symfony detail DTO has no `resourcesDiff`. This helper therefore takes
 * the first source that carries a number, in this order:
 *
 *   1. `event.resourcesDiff[<instructor resource id>]` — legacy name, in case
 *      the backend adds it.
 *   2. The instructor's own `eventResources[]` entry — `diffCounter`, `diff`,
 *      `counterDiff` or `total`.
 *   3. A row from `GET /counter/history/{eventId}` whose resource is the
 *      instructor — matched by resource id, `instructor` flag, or a
 *      type/name containing "instructor".
 *
 * PURE. Returns a 2-dp string or null; the caller decides whether to fetch
 * the history at all (only completed events with an instructor).
 *
 * @module instructorTotal
 */

const num = v => {
	if (v == null || v === '') return null;
	const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
	return Number.isFinite(n) ? n : null;
};

const fmt = n => n.toFixed(2);

const looksLikeInstructor = obj => {
	if (!obj || typeof obj !== 'object') return false;
	if (obj.instructor === true || obj.instructor === 1 || obj.instructor === '1') return true;
	if (obj.instructorId != null || obj.instructorID != null) return true;
	const res = obj.resource || obj;
	if (res?.user) return true;
	const t = res?.type;
	const typeName = typeof t === 'object' ? t?.name : t;
	const hay = `${typeName || ''} ${res?.typeName || ''} ${res?.typeUnique || ''}`.toLowerCase();
	return hay.includes('instructor');
};

/**
 * The instructor's RESOURCE id on this event (not the user id), if the DTO
 * exposes one.
 */
export function instructorResourceId(event) {
	if (!event) return null;
	const direct = event.instructorResourceId ?? event.instructorResourceID ?? null;
	if (direct != null) return String(direct);
	const list = Array.isArray(event.eventResources) ? event.eventResources : [];
	const er = list.find(looksLikeInstructor);
	if (!er) return null;
	const id = er.resourceID ?? er.resourceId ?? er.resource?.id ?? er.id ?? null;
	return id == null ? null : String(id);
}

/**
 * @param {Object|null} event    Detail event (normalized or raw).
 * @param {Object|Array|null} [history]  Unwrapped `/counter/history/{eventId}`
 *   payload — either an array of rows or `{ countersHistory: [...] }` /
 *   `{ resources: [...] }`. Optional.
 * @returns {string|null} e.g. `'3.00'`, or null when no source has a number.
 */
export function pickInstructorTotal(event, history = null) {
	if (!event) return null;
	const resId = instructorResourceId(event);
	const userId = event.instructorID ?? event.instructorId ?? event.instructor?.id ?? null;

	// 1. Legacy map, keyed by resource id (fall back to user id — the legacy
	//    PHP keyed by resource id, but older shapes were seen keyed by user).
	const diffMap = event.resourcesDiff;
	if (diffMap && typeof diffMap === 'object') {
		for (const key of [resId, userId]) {
			if (key == null) continue;
			const n = num(diffMap[String(key)]);
			if (n != null) return fmt(n);
		}
	}

	// 2. The instructor's eventResources entry.
	const list = Array.isArray(event.eventResources) ? event.eventResources : [];
	const er = list.find(looksLikeInstructor);
	if (er) {
		for (const k of ['diffCounter', 'diff', 'counterDiff', 'total']) {
			const n = num(er[k] ?? er.resource?.[k]);
			if (n != null) return fmt(n);
		}
	}

	// 3. Counter history rows.
	const rows = Array.isArray(history)
		? history
		: Array.isArray(history?.countersHistory)
			? history.countersHistory
			: Array.isArray(history?.resources)
				? history.resources
				: [];
	for (const row of rows) {
		const rowResId = row?.resourceID ?? row?.resourceId ?? row?.resource?.id ?? null;
		const isInstructor =
			(resId != null && rowResId != null && String(rowResId) === resId) || looksLikeInstructor(row);
		if (!isInstructor) continue;
		for (const k of ['diffCounter', 'diff', 'total', 'counterDiff']) {
			const n = num(row[k]);
			if (n != null) return fmt(n);
		}
		// Nested counters (aircraft-style history row): sum the diffs.
		if (Array.isArray(row.counters)) {
			const sum = row.counters.reduce((acc, c) => acc + (num(c?.diff) ?? 0), 0);
			if (row.counters.length && Number.isFinite(sum)) return fmt(sum);
		}
	}

	return null;
}

export default pickInstructorTotal;
